import type { ProjectBrief, Task } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { ScrollText } from 'lucide-react';
import { Age, RepoChip, SourceChip, TaskKey } from '@/components/common/bits';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { RequestPlanButton } from '@/features/actions/GateActions';
import type { RowProps } from '@/hooks/use-row-keys';
import { briefsAwaitingPlan } from '@/lib/briefs';
import { minutesSince } from '@/lib/format';
import { shortKey } from '@/lib/keys';

/**
 * Jobs nobody is planning.
 *
 * A filed job does not decompose itself: the hub never calls a model, so until a
 * person asks for a plan the job sits in `draft` and no agent will ever touch it.
 * The composer asks on your behalf, so what lands here came in from an intake
 * adapter, or had its automatic request fail.
 *
 * It used to sit on the home page, directly under the gate list, which read it as
 * a third kind of gate. It is not one: nothing is waiting on a decision, the work
 * is simply stalled before it starts. It belongs with the rest of a job's life,
 * which is this page.
 *
 * Nothing renders while there are none.
 */
export function NeedsPlan({
  briefs,
  tasks,
  /**
   * The page's roving focus, when the page has one.
   *
   * These rows are the first thing on Jobs and they used to be reachable from
   * `j` / `k` only because the same jobs were printed a second time in "In
   * progress" and *that* copy carried the focus. The duplicate is gone, so the
   * focus has to come here instead or the top region of the page is unreachable
   * from the keyboard.
   */
  rowProps,
}: {
  briefs: ProjectBrief[];
  tasks: Task[];
  rowProps?: (id: string) => RowProps;
}) {
  const waiting = briefsAwaitingPlan(briefs, tasks);
  if (!waiting.length) return null;

  return (
    <Section>
      <SectionHead
        title="Stalled before planning"
        hue="var(--dy-plan)"
        meta={`${waiting.length} draft${waiting.length === 1 ? '' : 's'} · no agent will pick these up`}
      />
      <Surface>
        <ul className="divide-line divide-y">
          {waiting.map((brief) => (
            <li
              key={brief.id}
              className="hover:bg-raised/60 flex items-start gap-3 px-gutter py-3 transition-colors duration-100"
            >
              <ScrollText className="text-plan mt-0.5 size-4 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <Link
                  to="/jobs/$briefId"
                  params={{ briefId: brief.id }}
                  {...rowProps?.(brief.id)}
                  className="hover:text-gate block min-w-0 truncate text-base transition-colors duration-100"
                  title={brief.title}
                >
                  {brief.title}
                </Link>
                <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                  <TaskKey>{shortKey(brief.id)}</TaskKey>
                  <RepoChip repo={brief.repos[0] ?? '-'} extra={brief.repos.length - 1} />
                  <SourceChip source={brief.source} />
                  <span className="text-fg-3 truncate text-xs">by {brief.submittedBy}</span>
                </div>
              </div>
              <span className="mt-0.5 shrink-0">
                <Age minutes={minutesSince(brief.createdAt)} />
              </span>
              <RequestPlanButton briefId={brief.id} className="shrink-0" />
            </li>
          ))}
        </ul>
      </Surface>
    </Section>
  );
}
